import { useEffect, useState } from 'react';
import Subtitle from 'components/Subtitle';
import App from 'pages/organization/settings/App';
import Button from 'components/Button';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { updateMemberOrganization } from 'api/portal';
import { setActiveOrganizationData, setOrganizations } from 'features/userSlice';
import { Box, TextField, Typography } from '@mui/material';
import { showErrorToast, showSuccessToast } from 'features/toastSlice';
import { useTranslation } from 'react-i18next';
import { castValueToNumber, dayFieldString, filterNumbers } from 'utils/inputs';
import { storedRefreshToExtraDayString } from 'utils/deviceJwtExpiration';
import Fade from 'components/Fade';

function OrganizationDeviceJwtExpiration() {
  const organization = useSelector((state) => state.user.active_organization);
  const organizations = useSelector((state) => state.user.organizations);

  const initialJwt = dayFieldString(organization.jwt_device_expiration);
  const initialRefresh = storedRefreshToExtraDayString(
    organization.jwt_device_expiration,
    organization.refresh_device_expiration,
  );

  const [jwtDays, setJwtDays] = useState(initialJwt);
  const [refreshDays, setRefreshDays] = useState(initialRefresh);
  const [errors, setErrors] = useState(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const p = 'organization.organization-settings.device-jwt-expiration';

  useEffect(() => {
    if (!organization?.id || organization.id === -1) {
      return;
    }
    setJwtDays(dayFieldString(organization.jwt_device_expiration));
    setRefreshDays(
      storedRefreshToExtraDayString(
        organization.jwt_device_expiration,
        organization.refresh_device_expiration,
      ),
    );
  }, [
    organization?.id,
    organization?.jwt_device_expiration,
    organization?.refresh_device_expiration,
  ]);

  const validateForm = () => {
    const nextErrors = new Map();
    const jwtNum = castValueToNumber(jwtDays);
    const refreshNum = castValueToNumber(refreshDays);

    if (jwtNum === undefined) {
      nextErrors.set('jwt', t(`${p}.field-required`));
    } else if (jwtNum < 1) {
      nextErrors.set('jwt', t(`${p}.access-token-min`));
    }

    if (refreshNum === undefined) {
      nextErrors.set('refresh', t(`${p}.field-required`));
    } else if (refreshNum < 30) {
      nextErrors.set('refresh', t(`${p}.refresh-token-min`));
    }

    return nextErrors;
  };

  const onSubmit = () => {
    const nextErrors = validateForm();
    setErrors(nextErrors);

    if (nextErrors.size > 0) {
      dispatch(showErrorToast('messages.fix-fields'));
      return;
    }

    const jwtNum = castValueToNumber(jwtDays);
    const refreshNum = castValueToNumber(refreshDays);
    const refreshTotal = jwtNum + refreshNum;

    setIsSubmitting(true);

    updateMemberOrganization({
      jwt_device_expiration: jwtNum,
      refresh_device_expiration: refreshTotal,
    })
      .then((response) => {
        const { data } = response;
        setIsSubmitting(false);
        dispatch(showSuccessToast('organization.organization-settings.save-success-message'));
        dispatch(setActiveOrganizationData(data));
        dispatch(
          setOrganizations(
            organizations.map((item) => (item.id === data.id ? data : item)),
          ),
        );
      })
      .catch(() => {
        setIsSubmitting(false);
        dispatch(showErrorToast('organization.organization-settings.save-failure-message'));
      });
  };

  return (
    <App>
      <Fade>
        <Box maxWidth="428px" marginBottom="48px">
          <Subtitle>{t(`${p}.subtitle`)}</Subtitle>

          <Typography variant="body2" color="secondary" marginBottom="24px">
            {t(`${p}.intro`)}
          </Typography>

          <TextField
            id="jwt_device_expiration"
            sx={{ marginTop: 2 }}
            value={jwtDays}
            onChange={(e) => setJwtDays(filterNumbers(e.target.value))}
            label={t(`${p}.access-token-label`)}
            variant="outlined"
            error={errors.has('jwt')}
            helperText={errors.get('jwt')}
            fullWidth
            inputProps={{ inputMode: 'numeric', 'data-testid': 'jwt-device-expiration-input' }}
          />

          <TextField
            id="refresh_device_expiration"
            sx={{ marginTop: 3 }}
            value={refreshDays}
            onChange={(e) => setRefreshDays(filterNumbers(e.target.value))}
            label={t(`${p}.refresh-token-label`)}
            variant="outlined"
            error={errors.has('refresh')}
            helperText={errors.get('refresh')}
            fullWidth
            inputProps={{ inputMode: 'numeric', 'data-testid': 'refresh-device-expiration-input' }}
          />

          <Button
            variant="outlined"
            loading={isSubmitting}
            onClick={onSubmit}
            sx={{ marginTop: 4 }}
            data-testid="save-button"
          >
            {t(`${p}.submit-button-text`)}
          </Button>
          <Button
            sx={{ marginLeft: 2, marginTop: 4 }}
            variant="outlined"
            onClick={() => navigate('/')}
            data-testid="cancel-button"
          >
            {t(`${p}.cancel-button-text`)}
          </Button>
        </Box>
      </Fade>
    </App>
  );
}

export default OrganizationDeviceJwtExpiration;
